import React, { useEffect, useRef, useState } from 'react';
import {
  getImageUrl,
  getImageVariant,
  getLocalizedName,
} from '../../../../utility/util/util.service';
import PropTypes from 'prop-types';
import { AvForm } from 'availity-reactstrap-validation';
import LocalizedNameField from '../../form/fields/LocalizedNameField';
import SaveSubmitButton from '../../form/buttons/SaveSubmitButton';
import ImageDropzoneComponent from '../../form/dropzones/image-dropzone/image-dropzone.component';
import {
  closeModalSafely,
  getTranslationsCollapsable,
  handleSubmit,
} from './create-or-edit-category.service';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  fetchMenuEntries,
  fetchItemCategories,
  fetchAreas,
} from '../../../../redux/store/store.actions';
import { connect } from 'react-redux';
import {
  execOnSuccess,
  setIsCreateCategoryModalOpen,
  setIsEditCategoryModalOpen,
} from '../../../../redux/modal/modal.actions';
import DropdownField from '../../form/fields/DropdownField';

const CreateOrEditCategoryModal = ({
  isCreateCategoryModalOpen,
  isEditCategoryModalOpen,
  createCategoryModalData,
  editCategoryModalData,
  intl,
  areas,
  store,

  fetchMenuEntries,
  setIsCreateCategoryModalOpen,
  setIsEditCategoryModalOpen,
  fetchItemCategories,
  fetchAreas,
}) => {
  const [targetCategory, setTargetCategory] = useState({});
  const [uploadImage, setUploadImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTranslationsOpen, setIsTranslationsOpen] = useState(false);
  const formRef = useRef(null);

  const isOpen = isCreateCategoryModalOpen || isEditCategoryModalOpen;
  const isEditMode = isEditCategoryModalOpen;
  const {
    parentCategoryId, // only mandatory when isEditMode=false
    category, // only mandatory when isEditMode=true
    onSuccess,
  } = (isEditMode ? editCategoryModalData : createCategoryModalData) || {};
  const closeModal = () =>
    isCreateCategoryModalOpen
      ? setIsCreateCategoryModalOpen(false)
      : setIsEditCategoryModalOpen(false);
  const userLocale = intl?.locale;
  const menuId = store?.menu?.id;
  const _closeModalSafely = () => closeModalSafely(isSubmitting, closeModal);
  const _onSubmit = (event, errors, values) =>
    handleSubmit(
      event,
      errors,
      values,
      isEditMode,
      uploadImage,
      targetCategory,
      parentCategoryId,
      async () => {
        return (
          await fetchItemCategories(menuId, true, undefined, store?.id),
          await fetchMenuEntries(menuId, true, () =>
            execOnSuccess(onSuccess),
          store?.id),
          fetchAreas()
        );
      },
      setIsSubmitting,
      setTargetCategory,
      closeModal,
      store?.id,
    );
  const _getTranslationsCollapsable = () =>
    getTranslationsCollapsable(
      targetCategory,
      isTranslationsOpen,
      setIsTranslationsOpen,
      null,
      true,
      userLocale,
      false,
      false,
      null,
      true,
    );

  useEffect(() => {
    if (isOpen) {
      setTargetCategory((isEditMode && category) || {});
      setUploadImage(null);
      const variant = getImageVariant('menuEntry');
      setPreviewUrl(
        isEditMode && category?.coverImage
          ? getImageUrl(category.coverImage, variant)
          : null,
      );
    } else {
      // reset modal on closing
      setUploadImage(null);
      setPreviewUrl(null);
      setTargetCategory({});
    }
  }, [category, isEditMode, isOpen]);

  return (
    <ModalSheet
      isOpen={isOpen}
      closeModal={_closeModalSafely}
      title={intl.formatMessage({
        id: isEditMode ? 'editCategoryTitle' : 'createNewCategoryTitle',
      })}
      footer={
        <div className="align-content-center">
          <SaveSubmitButton
            data-testid="saveNewCategory"
            className="m-auto"
            isSubmitting={isSubmitting}
            onClick={() => formRef?.current.handleSubmit()}
          />
        </div>
      }
    >
      <div className="px-1 mt-1">
        <AvForm ref={formRef} onSubmit={_onSubmit} disabled={isSubmitting}>
          <LocalizedNameField
            data-testid="nameCategoryInput"
            object={targetCategory}
            locale={userLocale}
            required
          />
          <DropdownField
            groupClassName="mb-50"
            isClearable={true}
            isMulti={true}
            options={areas}
            propertyName="areaIds"
            labelMessageId="areasLabel"
            getOptionValue={(area) => area?.id}
            values={areas
              ?.filter(({ categories }) =>
                categories.find(({ id }) => id === targetCategory?.id),
              )
              .map(({ id }) => id)}
            emptyValue={null}
            getOptionLabel={(area) => area?.name}
          />
          <span className="d-block font-small-2 mb-1 text-center">
            <FormattedMessage id="selectCategoryText" />
          </span>
          <ImageDropzoneComponent
            data-testid="imageCategoryCreation"
            propertyName="coverImage"
            object={targetCategory}
            setter={setTargetCategory}
            previewUrl={previewUrl}
            setPreviewUrl={setPreviewUrl}
            setUploadImage={setUploadImage}
            isDisabled={isSubmitting}
          />
          {_getTranslationsCollapsable()}
        </AvForm>
      </div>
    </ModalSheet>
  );
};

CreateOrEditCategoryModal.propTypes = {
  isCreateCategoryModalOpen: PropTypes.bool,
  isEditCategoryModalOpen: PropTypes.bool,
  createCategoryModalData: PropTypes.object,
  editCategoryModalData: PropTypes.object,
  intl: PropTypes.object,
  fetchMenuEntries: PropTypes.func,
  setIsCreateCategoryModalOpen: PropTypes.func,
  setIsEditCategoryModalOpen: PropTypes.func,
};

const mapStateToProps = (state) => ({
  isCreateCategoryModalOpen: state.modals.isCreateCategoryModalOpen,
  isEditCategoryModalOpen: state.modals.isEditCategoryModalOpen,
  createCategoryModalData: state.modals.createCategoryModalData,
  editCategoryModalData: state.modals.editCategoryModalData,
  areas: state.store.areas,
  store: state.store.store,
});

export default connect(mapStateToProps, {
  fetchMenuEntries,
  setIsCreateCategoryModalOpen,
  setIsEditCategoryModalOpen,
  fetchItemCategories,
  fetchAreas,
})(injectIntl(CreateOrEditCategoryModal));
